/* eslint-disable no-irregular-whitespace */
/* eslint-disable max-lines-per-function */
import { toNDigits } from '@mail/cross-sizes-utils';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    overquotaCommunicationPayPlacement,
    overquotaModalConfig,
    overquotaMoreInfoUrl,
} from 'reactApp/appHelpers/featuresHelpers/features/overquotaCommunications';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { useBuyClickHandlers } from 'reactApp/hooks/useBuyClickHandlers';
import { sendDwhModal } from 'reactApp/modules/dwh/dwh.helpers';
import { getUserOverquotaState } from 'reactApp/modules/profile/profile.selectors';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import Content from 'reactApp/ui/Content/Content';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { OverquotaSpaceBlock } from 'reactApp/ui/OverquotaSpaceBlock';
import { formatFileHistoryDate } from 'reactApp/utils/formatDate';
import { sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './OverquotaModal.css';
import { OverquotaBannerCtrl } from './OverquotaModal.helpers';

const ACTION_NOT_ENOUGH_SPACE = 'not-enough-space';

export type OverquotaModalProps = {
    onClose?: () => void;
};

const getModalName = (state?: string) => {
    return state === 'full_blocked' ? 'mnz_overquota_after_94_day' : 'overquota_splash_new';
};

export const OverquotaModal = memo(function OverquotaPopup({ onClose }: OverquotaModalProps): ReactElement | null {
    const dispatch = useDispatch();
    const storage = useSelector(getCurrentStorage);
    const { used, userTotalSpace } = useSelector(UserQuotaSelectors.getUserQuotaState);
    const overquota = useSelector(UserQuotaSelectors.getOverQuota);
    const { trashSize = 0, blockTime = undefined, state, clearTime } = useSelector(getUserOverquotaState);
    const theFirstStep = blockTime && state === 'started';
    const theSecondStep = blockTime && (state === 'blocked' || state === 'unblocked');

    const hasTrash = trashSize > 0;
    const hasClearButton = overquotaModalConfig.clearButton;
    const showModal = Boolean(used && userTotalSpace);

    const { showPaywall } = useBuyClickHandlers({ paySource: 'mnz_overquota_modal' });

    const modalName = getModalName(state);

    useEffect(() => {
        if (!showModal) {
            return;
        }
        sendDwhModal({ name: modalName, dwh_enabled_fields: [['name', 'button_name']] });
        sendPaymentGa({
            action: ACTION_NOT_ENOUGH_SPACE,
            label: 'view',
        });
    }, [showModal, modalName]);

    const title = useMemo(() => {
        if (blockTime && state === 'started') {
            return (
                <>
                    <span>{formatFileHistoryDate(blockTime)}</span> доступ к файлам будет ограничен, но есть решение
                </>
            );
        }

        if (clearTime && (state === 'unblocked' || state === 'full_blocked')) {
            return (
                <>
                    <span>{formatFileHistoryDate(clearTime)}</span> ваши файлы будут удалены. Как не допустить
                </>
            );
        }

        return 'Вашим файлам не хватает места';
    }, [blockTime, state, clearTime]);

    const content = useMemo(() => {
        if (hasTrash) {
            return (
                <>
                    Единое пространство Облака и Почты переполнено. Увеличьте его с подпиской Mail Space или очистите{' '}
                    <span>
                        {
                            toNDigits({
                                bytes: trashSize,
                                maxDigitsCount: 3,
                            }).value
                        }
                    </span>{' '}
                    в корзине
                </>
            );
        }

        if (blockTime && state === 'started') {
            return (
                <>
                    Единое пространство Облака и Почты переполнено на <span>{overquota.value}</span> — нельзя будет загружать, смотреть и
                    отправлять файлы. Увеличьте место с подпиской Mail Space.{' '}
                    <a href={overquotaMoreInfoUrl} target="_blank" rel="noopener noreferrer">
                        Подробнее
                    </a>
                </>
            );
        }

        if (clearTime && (state === 'unblocked' || state === 'full_blocked')) {
            return (
                <>
                    Единое пространство Облака и Почты переполнено на <span>{overquota.value}</span> — увеличьте его с подпиской Mail Space
                    {hasClearButton ? ' или удалите лишнее' : ''}. Если ничего не сделать, оно автоматически очистится.{' '}
                    <a href={overquotaMoreInfoUrl} target="_blank" rel="noopener noreferrer">
                        Подробнее
                    </a>
                </>
            );
        }

        return (
            <>
                Скоро они{' '}
                <a href={overquotaMoreInfoUrl} target="_blank" rel="noopener noreferrer">
                    будут удалены
                </a>
                . Увеличьте место, чтобы их сохранить, или очистите Облако от ненужных файлов
            </>
        );
    }, [overquota.value, hasTrash, blockTime, trashSize, clearTime, hasClearButton, state]);

    const clearButtonText = useMemo(() => {
        if (hasTrash) {
            return 'Очистить';
        }

        if (blockTime) {
            return 'Удалить лишнее';
        }

        return 'Очистить';
    }, [hasTrash, blockTime]);

    const buttonTitle = useMemo(() => {
        if (blockTime && state === 'started') {
            return 'Снять ограничение';
        }

        if (clearTime && (state === 'unblocked' || state === 'full_blocked')) {
            return 'Увеличить место';
        }

        return 'Увеличить место';
    }, [state, blockTime, clearTime]);

    const onPrimaryHandler = useCallback(() => {
        sendDwhModal({
            action: 'click',
            name: modalName,
            button_name: OverquotaBannerCtrl.add_space,
            dwh_enabled_fields: [['name', 'button_name']],
        });

        let search = PaymentUTM.overquotaSplashNew;

        if (theFirstStep) {
            search = PaymentUTM.overquotaPopupBlockFirstStepIncreaseSpace;
        }

        if (theSecondStep) {
            search = PaymentUTM.overquotaPopupBlockSecondStepIncreaseSpace;
        }

        if (state === 'full_blocked') {
            search = PaymentUTM.overquotaFullBlockedNewModal;
        }

        if (overquotaCommunicationPayPlacement === 'paywall') {
            showPaywall();
        } else {
            dispatch(historyPush({ id: '/promo/quota', search: `?${search}` }));
        }

        onClose?.();
    }, [onClose, storage, dispatch, state, modalName]);

    const onSecondaryHandler = useCallback(() => {
        sendDwhModal({
            action: 'click',
            name: modalName,
            button_name: OverquotaBannerCtrl.clear,
            dwh_enabled_fields: [['name', 'button_name']],
        });

        let search = '';

        if (theFirstStep) {
            search = PaymentUTM.overquotaPopupBlockFirstStepClearSpace;
        }
        if (theSecondStep) {
            search = PaymentUTM.overquotaPopupBlockSecondStepClearSpace;
        }

        if (hasTrash) {
            dispatch(
                historyPush({
                    id: EStorageType.trashbin,
                    search: `?${search}`,
                })
            );
        } else {
            dispatch(
                historyPush({
                    id: '/promo/quota',
                    search: `?${search}`,
                    hash: 'cleaner',
                })
            );
        }
        onClose?.();
    }, [onClose, storage, dispatch, hasTrash, modalName]);

    const onCloseHandler = useCallback(() => {
        sendDwhModal({ action: 'close', name: modalName, dwh_enabled_fields: [['name', 'button_name']] });

        onClose?.();
    }, [onClose, storage, modalName]);

    if (!showModal) {
        return null;
    }

    return (
        <Dialog
            open
            header={''}
            closeOnDimmerClick={false}
            id="overquota-alert"
            qaId="overquota-alert"
            onCancel={onCloseHandler}
            className={styles.root}
            size="tiny"
        >
            <Content isModal>
                <div className={styles.content}>
                    <div className={styles.imageWrapper}>
                        <OverquotaSpaceBlock />
                    </div>
                    <div className={styles.title}>{title}</div>
                    <div className={styles.text}>{content}</div>
                    <div className={styles.footerWrapper}>
                        <Button
                            className={styles.button}
                            data-name="freeze-renewal"
                            theme="octavius"
                            sizeMode={ButtonSizeMode.small}
                            onClick={onPrimaryHandler}
                            primary={true}
                            error={false}
                        >
                            {buttonTitle}
                        </Button>
                        {hasClearButton && (
                            <Button
                                className={styles.button}
                                onClick={onSecondaryHandler}
                                theme="octavius"
                                sizeMode={ButtonSizeMode.small}
                                data-name="secondary"
                            >
                                {clearButtonText}
                            </Button>
                        )}
                    </div>
                </div>
            </Content>
        </Dialog>
    );
});

OverquotaModal.displayName = 'OverquotaModal';
